/**
 * Copiar los datos de una sección al portapapeles como TEXTO. Complementa la exportación a CSV: en vez
 * de guardar un archivo, deja los datos listos para PEGAR en WhatsApp, correo o Excel (separado por
 * tabulaciones → cae en columnas). Pensado sobre todo para el teléfono, donde bajar un CSV es incómodo.
 * La construcción del texto es pura y testeable; la copia al portapapeles se aísla aparte.
 */
export interface CopyableMetric { readonly label: string; readonly value: string; readonly deltaPct?: number }
export interface CopyableRow { readonly primary: string; readonly secondary: string; readonly status: { readonly label: string }; readonly amount: string }
export interface CopyableOverview {
  readonly title: string;
  readonly columns: readonly string[];
  readonly rows: readonly CopyableRow[];
  readonly metrics?: readonly CopyableMetric[];
  readonly periodLabel?: string;
}

/** Arma el texto para el portapapeles: título + período + métricas + tabla (TSV). */
export function overviewToText(o: CopyableOverview): string {
  const lines: string[] = [o.title];
  if (o.periodLabel) lines.push(o.periodLabel);
  if (o.metrics && o.metrics.length > 0) {
    lines.push("");
    for (const m of o.metrics) {
      const delta = typeof m.deltaPct === "number" ? ` (${m.deltaPct > 0 ? "+" : ""}${m.deltaPct}%)` : "";
      lines.push(`${m.label}: ${m.value}${delta}`);
    }
  }
  if (o.columns.length > 0 && o.rows.length > 0) {
    lines.push("");
    lines.push(o.columns.join("\t"));
    for (const r of o.rows) lines.push([r.primary, r.secondary, r.status.label, r.amount].join("\t"));
  }
  return lines.join("\n");
}

/**
 * Copia texto al portapapeles. Usa la API moderna (contexto seguro: Electron y HTTPS la tienen) y cae a
 * un textarea temporal + execCommand si no está disponible. Devuelve true si logró copiar.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* cae al método antiguo */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}
